import { cachedRequest } from './requestCache';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Simple error handling utility
const handleApiError = (error: any): Error => {
  if (error instanceof Error) {
    return error;
  }
  return new Error(String(error));
};

// Interface definitions for API responses
export interface SopDeviationSummary {
  business_identifier?: string;
  total_analyzed_conversations: number;
  compliance_percentage: number;
  compliant_count: number;
  non_compliant_count: number;
  trend: number;
  status: 'good' | 'warning' | 'critical';
}

export interface DealSentimentSummary {
  business_identifier?: string;
  total_analyzed_conversations: number;
  average_sentiment_score: number;
  positive_sentiment_count: number;
  neutral_sentiment_count: number;
  negative_sentiment_count: number;
  trend: number;
  status: 'excellent' | 'good' | 'needs_attention';
}

export interface CallQualitySummary {
  business_identifier?: string;
  total_analyzed_calls: number;
  average_quality_score: number;
  average_clarity_score: number;
  average_engagement_score: number;
  average_professionalism_score: number;
  high_quality_count: number;
  medium_quality_count: number;
  low_quality_count: number;
  trend: number;
  status: 'excellent' | 'good' | 'needs_improvement';
}

// Individual conversation metrics
export interface ConversationSopDeviation {
  id: string;
  conversation_id: string;
  sop_followed_overall: boolean | null;
  sop_deviation_percentage: number | null;
  weighted_stage_score: number | null;
  overall_deviation_summary: string | null;
  stage_details: Array<{
    id: string;
    stage_name: string | null;
    sop_requirement: string | null;
    status: string | null;
    justification: string | null;
  }>;
}

export interface ConversationDealSentiment {
  id: string;
  conversation_id: string;
  overall_deal_sentiment_text: string | null;
  overall_deal_sentiment_score: number | null;
  buyer_interest_level_score: number | null;
  buyer_interest_level: string | null;
  buyer_interest_level_text?: string; // API may return this field
  positive_sentiment_drivers: string[];
  negative_sentiment_drivers: string[];
  key_points: string[];
}

export interface ConversationCallQuality {
  id: string;
  conversation_id: string;
  overall_call_quality_score: number | null;
  clarity_score: number | null;
  engagement_score: number | null;
  professionalism_score: number | null;
  call_quality_summary: string | null;
  improvement_areas: Array<{
    id: string;
    area_description: string | null;
  }>;
}

export interface TalkListenRatio {
  id: string;
  conversation_id: string;
  sales_person_talk_percentage: number | null;
  client_talk_percentage: number | null;
  dominant_speaker: string | null;
  analysis: string | null;
}

export interface SalespersonMetricsSummary {
  salesperson_id: string;
  total_conversations: number;
  sop_adherence_percentage: number;
  weighted_stage_score: number;
  objection_handling_score: number;
  average_talk_percentage: number;
  average_listen_percentage: number;
  dominant_speaker_ratio: string;
  trend: {
    sop_adherence: number;
    objection_handling: number;
    talk_listen_balance: number;
  };
}

export interface ObjectionHandlingScore {
  conversation_id: string;
  objection_handling_score: number;
  total_objections: number;
  resolved_objections: number;
  unresolved_objections: number;
  handling_effectiveness: string;
}

export interface ConversationActionItem {
  id: string;
  conversation_id: string;
  action_text: string;
  category: 'sop_compliance' | 'call_quality' | 'objection_resolution' | 'general';
  priority: 'high' | 'medium' | 'low';
  source: string;
}

class MetricsService {
  private async makeRequest<T>(url: string, options?: RequestInit): Promise<T> {
    const method = (options?.method || 'GET').toUpperCase();
    // Only GET responses are safe to cache/de-duplicate (POST bodies vary).
    if (method === 'GET') {
      const key = `metrics GET ${url}`;
      return cachedRequest(key, () => this.doRequest<T>(url, options));
    }
    return this.doRequest<T>(url, options);
  }

  private async doRequest<T>(url: string, options?: RequestInit): Promise<T> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Add API key for conversation summariser endpoints
      if (url.includes('staging.conversation.convrsespaces.com') || 
          url.includes('localhost:8000') || 
          API_BASE_URL.includes('conversation')) {
        headers['X-API-Key'] = 'h-HdEURZOkbVll1-skwSiK9m1kAaRdXIpss5wl7d0EM';
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
        ...options,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[MetricsService] HTTP error! status: ${response.status}, response: ${errorText}`);
        throw new Error(`HTTP error! status: ${response.status}, response: ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`[MetricsService] Request failed for ${url}:`, error);
      throw handleApiError(error);
    }
  }

  // Business-level aggregated metrics
  async getSopDeviationSummary(businessIdentifier?: string, timeRange: string = '7d'): Promise<SopDeviationSummary> {
    const endpoint = businessIdentifier 
      ? `/aggregations/business/${businessIdentifier}/sop_deviation_summary?time_range=${timeRange}`
      : '/aggregations/overall/sop_deviation_summary';
    
    return this.makeRequest(`${API_BASE_URL}${endpoint}`);
  }

  async getDealSentimentSummary(businessIdentifier?: string, timeRange: string = '7d'): Promise<DealSentimentSummary> {
    const endpoint = businessIdentifier 
      ? `/aggregations/business/${businessIdentifier}/deal_sentiment_summary?time_range=${timeRange}`
      : '/aggregations/overall/deal_sentiment_summary';
    
    return this.makeRequest(`${API_BASE_URL}${endpoint}`);
  }

  async getCallQualitySummary(businessIdentifier?: string, timeRange: string = '7d'): Promise<CallQualitySummary> {
    const endpoint = businessIdentifier 
      ? `/aggregations/business/${businessIdentifier}/call_quality_summary?time_range=${timeRange}`
      : '/aggregations/overall/call_quality_summary';
    
    return this.makeRequest(`${API_BASE_URL}${endpoint}`);
  }

  // Individual conversation metrics
  async getConversationSopDeviation(conversationId: string): Promise<ConversationSopDeviation> {
    return this.makeRequest(`${API_BASE_URL}/aggregations/conversation/${conversationId}/sop_deviation`);
  }

  async getConversationDealSentiment(conversationId: string): Promise<ConversationDealSentiment> {
    return this.makeRequest(`${API_BASE_URL}/aggregations/conversation/${conversationId}/deal_sentiment`);
  }

  async getConversationCallQuality(conversationId: string): Promise<ConversationCallQuality> {
    return this.makeRequest(`${API_BASE_URL}/aggregations/conversation/${conversationId}/call_quality_score`);
  }

  async getConversationTalkListenRatio(conversationId: string): Promise<TalkListenRatio> {
    return this.makeRequest(`${API_BASE_URL}/aggregations/conversation/${conversationId}/talk_listen_ratio`);
  }

  async getConversationObjectionHandlingScore(conversationId: string): Promise<ObjectionHandlingScore> {
    return this.makeRequest(`${API_BASE_URL}/aggregations/conversation/${conversationId}/objection_handling_score`);
  }

  // Get action items from conversation data
  async getConversationActionItems(conversationId: string): Promise<ConversationActionItem[]> {
    try {
      const [sopDeviation, callQuality, objections] = await Promise.all([
        this.getConversationSopDeviation(conversationId).catch(() => null),
        this.getConversationCallQuality(conversationId).catch(() => null),
        this.makeRequest<any[]>(`${API_BASE_URL}/aggregations/conversation/${conversationId}/top_objections`).catch(() => [])
      ]);

      const actionItems: ConversationActionItem[] = [];

      // Extract action items from SOP deviations
      if (sopDeviation && sopDeviation.stage_details) {
        sopDeviation.stage_details.forEach((stage, index) => {
          if (stage.status === 'not_followed' || stage.status === 'partially_followed') {
            actionItems.push({
              id: `sop-${stage.id}`,
              conversation_id: conversationId,
              action_text: `Improve SOP compliance for ${stage.stage_name}: ${stage.justification || 'Follow required procedures'}`,
              category: 'sop_compliance',
              priority: stage.status === 'not_followed' ? 'high' : 'medium',
              source: 'SOP Analysis'
            });
          }
        });
      }

      // Extract action items from call quality improvement areas
      if (callQuality && callQuality.improvement_areas) {
        callQuality.improvement_areas.forEach((area, index) => {
          if (area.area_description) {
            actionItems.push({
              id: `quality-${area.id}`,
              conversation_id: conversationId,
              action_text: area.area_description,
              category: 'call_quality',
              priority: callQuality.overall_call_quality_score && callQuality.overall_call_quality_score < 60 ? 'high' : 'medium',
              source: 'Call Quality Analysis'
            });
          }
        });
      }

      // Extract action items from unresolved objections
      if (objections && objections.length > 0) {
        objections.forEach((objection, index) => {
          if (objection.resolution_status === 'unresolved' || objection.resolution_status === 'partial') {
            actionItems.push({
              id: `objection-${objection.id}`,
              conversation_id: conversationId,
              action_text: `Address objection: ${objection.objection_text}`,
              category: 'objection_resolution',
              priority: 'high',
              source: 'Objection Analysis'
            });
          }
        });
      }

      return actionItems;
    } catch (error) {
      console.error(`Error fetching action items for conversation ${conversationId}:`, error);
      return [];
    }
  }

  // Get action items for multiple conversations
  async getMultipleConversationActionItems(conversationIds: string[]): Promise<ConversationActionItem[]> {
    try {
      const actionItemsPromises = conversationIds.map(id => 
        this.getConversationActionItems(id).catch(() => [])
      );
      
      const allActionItems = await Promise.all(actionItemsPromises);
      return allActionItems.flat();
    } catch (error) {
      console.error('Error fetching multiple conversation action items:', error);
      return [];
    }
  }

  // For meeting cards - get multiple conversation metrics
  async getMultipleConversationMetrics(conversationIds: string[]): Promise<{
    dealSentiments: ConversationDealSentiment[];
    callQualities: ConversationCallQuality[];
    sopDeviations: ConversationSopDeviation[];
  }> {
    const dealSentimentsPromise = this.makeRequest<ConversationDealSentiment[]>(
      `${API_BASE_URL}/aggregations/conversations/deal_sentiments`, 
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversation_ids: conversationIds })
      }
    );

    // Note: You may need to add similar POST endpoints for call_quality and sop_deviation
    // For now, we'll fetch them individually
    const callQualitiesPromise = Promise.all(
      conversationIds.map(id => this.getConversationCallQuality(id).catch(() => null))
    ).then(results => results.filter(Boolean));

    const sopDeviationsPromise = Promise.all(
      conversationIds.map(id => this.getConversationSopDeviation(id).catch(() => null))
    ).then(results => results.filter(Boolean));

    const [dealSentiments, callQualities, sopDeviations] = await Promise.all([
      dealSentimentsPromise,
      callQualitiesPromise,
      sopDeviationsPromise
    ]);

    return {
      dealSentiments,
      callQualities: callQualities as ConversationCallQuality[],
      sopDeviations: sopDeviations as ConversationSopDeviation[]
    };
  }

  // Salesperson-specific aggregated metrics
  async getSalespersonMetricsSummary(salespersonId: string | number): Promise<SalespersonMetricsSummary> {
    return this.makeRequest(`${API_BASE_URL}/aggregations/salesperson/${salespersonId}/metrics_summary`);
  }
}

export const metricsService = new MetricsService(); 